import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, EMPTY, tap } from 'rxjs';

import { EmailNotificationPreference } from '../models/email-notification.model';
import { EmailNotificationService } from '../services/data-services/email-notification.service';
import {
  LoadEmailNotificationError,
  LoadEmailNotificationPreference,
  LoadEmailNotificationPreferenceSuccess,
  ResetEmailNotificationState,
  UpdateEmailNotification,
  UpdateEmailNotificationError,
  UpdateEmailNotificationSuccess,
} from './actions';

export interface EmailNotificationStateModel {
  notificationsList: EmailNotificationPreference[];
  isLoading: boolean;
  loadError: string | null;
  isUpdating: boolean;
  updateError: string | null;
}

const defaultState: EmailNotificationStateModel = {
  notificationsList: [],
  isLoading: false,
  loadError: null,
  isUpdating: false,
  updateError: null,
};

@State<EmailNotificationStateModel>({
  name: 'emailNotification',
  defaults: defaultState,
})
@Injectable()
export class EmailNotificationState {
  private readonly emailNotificationService = inject(EmailNotificationService);

  @Action(LoadEmailNotificationPreference)
  loadEmailNotificationPreference(
    ctx: StateContext<EmailNotificationStateModel>,
  ) {
    ctx.patchState({ isLoading: true, loadError: null });

    return this.emailNotificationService.getEmailNotificationPreferences().pipe(
      tap((notificationResponse) => {
        ctx.dispatch(
          new LoadEmailNotificationPreferenceSuccess(
            notificationResponse.data ?? [],
          ),
        );
      }),
      catchError((error: Error) => {
        ctx.dispatch(
          new LoadEmailNotificationError(
            error?.message || 'Load preferences failed',
          ),
        );

        return EMPTY;
      }),
    );
  }

  @Action(LoadEmailNotificationPreferenceSuccess)
  loadEmailNotificationPreferenceSuccess(
    ctx: StateContext<EmailNotificationStateModel>,
    action: LoadEmailNotificationPreferenceSuccess,
  ) {
    ctx.patchState({
      notificationsList: action.notifications,
      loadError: null,
      isLoading: false,
    });
  }

  @Action(ResetEmailNotificationState)
  resetEmailNotificationState(ctx: StateContext<EmailNotificationStateModel>) {
    ctx.setState(defaultState);
  }

  @Action(LoadEmailNotificationError)
  loadEmailNotificationError(
    ctx: StateContext<EmailNotificationStateModel>,
    action: LoadEmailNotificationError,
  ) {
    ctx.patchState({
      notificationsList: [],
      loadError: action.error,
      isLoading: false,
    });
  }

  @Action(UpdateEmailNotification)
  updateEmailNotificationPreferences(
    ctx: StateContext<EmailNotificationStateModel>,
    { notifications }: UpdateEmailNotification,
  ) {
    ctx.patchState({ isUpdating: true, updateError: null });

    return this.emailNotificationService
      .postEmailNotificationPreferences(notifications)
      .pipe(
        tap(() => {
          ctx.dispatch(new UpdateEmailNotificationSuccess(notifications));
        }),
        catchError((error: Error) => {
          ctx.dispatch(
            new UpdateEmailNotificationError(
              error?.message || 'Update preferences failed',
            ),
          );

          return EMPTY;
        }),
      );
  }

  @Action(UpdateEmailNotificationSuccess)
  updateEmailNotificationSuccess(
    ctx: StateContext<EmailNotificationStateModel>,
    { notifications }: UpdateEmailNotificationSuccess,
  ) {
    ctx.patchState({
      notificationsList: notifications,
      updateError: null,
      isUpdating: false,
    });
  }

  @Action(UpdateEmailNotificationError)
  updateEmailNotificationError(
    ctx: StateContext<EmailNotificationStateModel>,
    action: UpdateEmailNotificationError,
  ) {
    ctx.patchState({
      updateError: action.error,
      isUpdating: false,
    });
  }
}
